import UserAvatar from './user-avatar';
import { useUserSettings } from '../utils/use-user-settings';
import { isFeatureEnabled } from '../utils/feature-flags';
import AvatarChipOld from './avatar-chip-old';

import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import Skeleton from '@mui/material/Skeleton';
import AddToDriveOutlined from '@mui/icons-material/AddToDriveOutlined';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import Code from '@mui/icons-material/Code';
import EventNoteOutlined from '@mui/icons-material/EventNoteOutlined';
import Folder from '@mui/icons-material/Folder';
import Groups from '@mui/icons-material/Groups';
import Lan from '@mui/icons-material/Lan';
import MiscellaneousServices from '@mui/icons-material/MiscellaneousServices';

interface AvatarChipProps {
  email: string;
  type:
    | 'User'
    | 'Group'
    | 'Domain'
    | 'gPanel Role'
    | 'OrgUnit'
    | 'Shared Drive'
    | 'RegEx'
    | 'User Drive'
    | 'Resource Calendar'
    | 'Roles'
    | 'Unknown';
  onDelete?: () => void;
  size?: 'small' | 'medium';
  label?: string;
  tooltip?: string;
  isLoading?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

export default function AvatarChip({
  email,
  type,
  onDelete,
  size = 'small',
  label,
  tooltip,
  isLoading,
  onClick,
  disabled = false,
}: AvatarChipProps) {
  const theme = useTheme();
  const { features } = useUserSettings();
  const chipSx = {
    width: 'fit-content',
    backgroundColor: theme.palette.semantic.tags.default,
    color: theme.palette.semantic.text.title2,
    border: '1px solid',
    borderColor: theme.palette.semantic.tags.border,
    ':hover': {
      backgroundColor: onClick
        ? theme.palette.semantic.tags.hovered
        : theme.palette.semantic.tags.default,
    },
    '& .MuiChip-deleteIcon': {
      color: theme.palette.semantic.tags.iconBg,
    },
  };

  const iconSx = {
    color: theme.palette.semantic.tags.icon,
    fontSize: 12,
  };
  const avatarSx = {
    backgroundColor: theme.palette.semantic.tags.iconBg,
  };

  if (!isFeatureEnabled('uiEnhancementPhase2', features)) {
    return (
      <AvatarChipOld
        email={email}
        type={type}
        onDelete={onDelete}
        size={size}
        label={label}
        tooltip={tooltip}
        isLoading={isLoading}
        onClick={onClick}
        disabled={disabled}
      />
    );
  }

  if (type === 'Group') {
    return (
      <Tooltip title={label ? label : email}>
        <Chip
          key={email}
          label={label ? label : email}
          size={size}
          avatar={<Avatar children={<Groups sx={iconSx} />} sx={avatarSx} />}
          onDelete={onDelete ? onDelete : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          clickable={onClick ? true : false}
          sx={chipSx}
          disabled={disabled}
        />
      </Tooltip>
    );
  } else if (type === 'Domain' || type.includes('Org')) {
    return (
      <Tooltip title={label ? label : email}>
        <Chip
          key={email}
          label={label ? label : email}
          size={size}
          avatar={<Avatar children={<Lan sx={iconSx} />} sx={avatarSx} />}
          onDelete={onDelete ? onDelete : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          clickable={onClick ? true : false}
          sx={chipSx}
          disabled={disabled}
        />
      </Tooltip>
    );
  } else if (type === 'gPanel Role') {
    return (
      <Tooltip title={label ? label : email}>
        <Chip
          key={email}
          label={label ? label : email}
          size={size}
          avatar={
            <Avatar
              children={<AdminPanelSettings sx={iconSx} />}
              sx={avatarSx}
            />
          }
          onDelete={onDelete ? onDelete : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          clickable={onClick ? true : false}
          sx={chipSx}
          disabled={disabled}
        />
      </Tooltip>
    );
  } else if (type === 'Shared Drive') {
    return (
      <Tooltip title={label ? label : email}>
        <Chip
          key={email}
          label={label ? label : email}
          size={size}
          avatar={
            <Avatar
              children={<AddToDriveOutlined sx={iconSx} />}
              sx={avatarSx}
            />
          }
          onDelete={onDelete ? onDelete : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          clickable={onClick ? true : false}
          sx={chipSx}
          disabled={disabled}
        />
      </Tooltip>
    );
  } else if (type === 'RegEx') {
    return (
      <Tooltip title={label ? label : email}>
        <Chip
          key={email}
          label={label ? label : email}
          size={size}
          avatar={<Avatar children={<Code sx={iconSx} />} sx={avatarSx} />}
          onDelete={onDelete ? onDelete : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          clickable={onClick ? true : false}
          sx={chipSx}
          disabled={disabled}
        />
      </Tooltip>
    );
  } else if (type === 'User Drive') {
    return (
      <Tooltip title={label ? label : email}>
        <Chip
          key={email}
          label={label ? label : email}
          size={size}
          avatar={<Avatar children={<Folder sx={iconSx} />} sx={avatarSx} />}
          onDelete={onDelete ? onDelete : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          clickable={onClick ? true : false}
          sx={chipSx}
          disabled={disabled}
        />
      </Tooltip>
    );
  } else if (type === 'Resource Calendar') {
    return (
      <Tooltip title={tooltip ? tooltip : label ? label : email}>
        <Chip
          key={email}
          label={
            isLoading ? (
              <Skeleton variant="text" width={100} height={20} />
            ) : label ? (
              label
            ) : (
              email
            )
          }
          size={size}
          avatar={
            <Avatar
              children={<EventNoteOutlined sx={iconSx} />}
              sx={avatarSx}
            />
          }
          onDelete={onDelete ? onDelete : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          clickable={onClick ? true : false}
          sx={chipSx}
          disabled={disabled}
        />
      </Tooltip>
    );
  } else if (type === 'Roles') {
    return (
      <Tooltip title={label ? label : email}>
        <Chip
          key={email}
          label={label ? label : email}
          size={size}
          avatar={
            <Avatar
              children={<MiscellaneousServices sx={iconSx} />}
              sx={avatarSx}
            />
          }
          onDelete={onDelete ? onDelete : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          clickable={onClick ? true : false}
          sx={chipSx}
          disabled={disabled}
        />
      </Tooltip>
    );
  } else if (type === 'Unknown') {
    return (
      <Tooltip title={label ? label : email}>
        <Chip
          key={email}
          label={label ? label : email}
          size={size}
          onDelete={onDelete ? onDelete : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          clickable={onClick ? true : false}
          sx={chipSx}
          disabled={disabled}
        />
      </Tooltip>
    );
  } else {
    return (
      <Tooltip title={tooltip ? tooltip : label ? label : email}>
        <Chip
          key={email}
          label={label ? label : email}
          size={size}
          avatar={<UserAvatar userEmail={email} />}
          onDelete={onDelete ? onDelete : undefined}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          clickable={onClick ? true : false}
          sx={chipSx}
          disabled={disabled}
        />
      </Tooltip>
    );
  }
}
